import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { IntensityGaugeSlider } from './IntensityGauge';
import { RssSourcePicker } from './RssSourcePicker';

const PLATFORM_OPTIONS = [
  { value: 'reddit', label: 'Reddit' },
  { value: 'bluesky', label: 'Bluesky' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'rss', label: 'RSS' },
];

export interface FenceEditable {
  id?: string;
  name: string;
  keywords: string[];
  blocked_users: string[];
  blocked_hashtags: string[];
  blocked_accounts: string[];
  fence_new_accounts: boolean;
  new_account_threshold_days: number;
  fence_low_followers: boolean;
  min_follower_threshold: number | null;
  platforms: string[];
  rss_feeds: string[];
  intensity_gauge: number;
}

interface FenceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialValue?: FenceEditable;
  onSaved: () => void;
}

function ChipInput({
  label,
  hint,
  values,
  onChange,
  placeholder,
}: {
  label: string;
  hint?: string;
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const t = draft.trim();
    if (!t) return;
    if (values.includes(t)) {
      setDraft('');
      return;
    }
    onChange([...values, t]);
    setDraft('');
  };
  return (
    <div>
      <Label>{label}</Label>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      <Input
        className="mt-1"
        placeholder={placeholder}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            add();
          }
        }}
        onBlur={add}
      />
      {values.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {values.map((v, i) => (
            <Badge key={`${v}-${i}`} variant="outline" className="gap-1 pr-1">
              <span className="text-xs">{v}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-auto p-0.5"
                onClick={() => onChange(values.filter((x) => x !== v))}
              >
                <X className="w-3 h-3" />
              </Button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

export const FenceDialog: React.FC<FenceDialogProps> = ({
  open,
  onOpenChange,
  initialValue,
  onSaved,
}) => {
  const { user } = useAuth();
  const editing = Boolean(initialValue?.id);

  const [name, setName] = useState('');
  const [keywords, setKeywords] = useState<string[]>([]);
  const [blockedUsers, setBlockedUsers] = useState<string[]>([]);
  const [blockedHashtags, setBlockedHashtags] = useState<string[]>([]);
  const [blockedAccounts, setBlockedAccounts] = useState<string[]>([]);
  const [fenceNewAccounts, setFenceNewAccounts] = useState(false);
  const [newAccountDays, setNewAccountDays] = useState(30);
  const [fenceLowFollowers, setFenceLowFollowers] = useState(false);
  const [minFollowers, setMinFollowers] = useState<number>(50);
  const [platforms, setPlatforms] = useState<string[]>([]);
  const [rssFeeds, setRssFeeds] = useState<string[]>([]);
  const [gauge, setGauge] = useState(3);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(initialValue?.name ?? '');
    setKeywords(initialValue?.keywords ?? []);
    setBlockedUsers(initialValue?.blocked_users ?? []);
    setBlockedHashtags(initialValue?.blocked_hashtags ?? []);
    setBlockedAccounts(initialValue?.blocked_accounts ?? []);
    setFenceNewAccounts(initialValue?.fence_new_accounts ?? false);
    setNewAccountDays(initialValue?.new_account_threshold_days ?? 30);
    setFenceLowFollowers(initialValue?.fence_low_followers ?? false);
    setMinFollowers(initialValue?.min_follower_threshold ?? 50);
    setPlatforms(initialValue?.platforms ?? []);
    setRssFeeds(initialValue?.rss_feeds ?? []);
    setGauge(initialValue?.intensity_gauge ?? 3);
  }, [open, initialValue]);

  const togglePlatform = (id: string, on: boolean) => {
    setPlatforms((prev) => (on ? [...new Set([...prev, id])] : prev.filter((p) => p !== id)));
  };

  useEffect(() => {
    if (rssFeeds.length > 0 && !platforms.includes('rss')) {
      setPlatforms((prev) => [...prev, 'rss']);
    }
  }, [rssFeeds, platforms]);

  const handleSave = async () => {
    if (!user) return;
    if (!name.trim()) {
      toast.error('Rule name is required');
      return;
    }
    if (
      keywords.length === 0 &&
      blockedUsers.length === 0 &&
      blockedHashtags.length === 0 &&
      blockedAccounts.length === 0 &&
      !fenceNewAccounts &&
      !fenceLowFollowers
    ) {
      toast.error('Add at least one filter — keywords, accounts, hashtags, or a heuristic toggle.');
      return;
    }
    setSaving(true);
    try {
      const payload: any = {
        user_id: user.id,
        name: name.trim(),
        rule_type: 'filter',
        keywords: keywords.length ? keywords : null,
        blocked_users: blockedUsers.length ? blockedUsers : null,
        blocked_hashtags: blockedHashtags.length ? blockedHashtags : null,
        blocked_accounts: blockedAccounts.length ? blockedAccounts : null,
        fence_new_accounts: fenceNewAccounts,
        new_account_threshold_days: newAccountDays,
        fence_low_followers: fenceLowFollowers,
        min_follower_threshold: fenceLowFollowers ? minFollowers : null,
        platforms: platforms.length ? platforms : null,
        rss_feeds: rssFeeds.length ? rssFeeds : null,
        intensity_gauge: gauge,
        intensity: gauge >= 3 ? 'hide' : gauge >= 1 ? 'deemphasize' : null,
        is_active: gauge > 0,
      };

      const result = initialValue?.id
        ? await supabase.from('filtering_rules').update(payload).eq('id', initialValue.id)
        : await supabase.from('filtering_rules').insert(payload);

      if (result.error) {
        toast.error(`Failed to save fence: ${result.error.message}`);
        return;
      }
      toast.success(editing ? 'Fence updated' : 'Fence created');
      onOpenChange(false);
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Fence' : 'New Fence'}</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Hide or de-emphasize content matching these keywords, accounts, or hashtags. The
            intensity gauge controls how aggressively the fence acts.
          </p>
        </DialogHeader>

        <div className="space-y-6">
          <div>
            <Label htmlFor="fence-name">Rule name</Label>
            <Input
              id="fence-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Crypto spam, Doomscrolling"
            />
          </div>

          <ChipInput
            label="Keywords / topics to block"
            hint="Press Enter or comma to add. Matches are case-insensitive on title/body."
            values={keywords}
            onChange={setKeywords}
            placeholder="spam, drama, advertorial"
          />

          <IntensityGaugeSlider mode="fence" value={gauge} onChange={setGauge} />

          <ChipInput
            label="Accounts to block"
            hint="No need to include u/ or @ — matches your feed authors regardless of prefix."
            values={blockedUsers}
            onChange={setBlockedUsers}
            placeholder="spammeruser"
          />

          <ChipInput
            label="Hashtags to block"
            hint="With or without #."
            values={blockedHashtags}
            onChange={setBlockedHashtags}
            placeholder="#nft"
          />

          <ChipInput
            label="Block accounts (cross-platform)"
            hint="Hard hide. Anything from these handles gets fenced regardless of content. Matches across every connected platform."
            values={blockedAccounts}
            onChange={setBlockedAccounts}
            placeholder="someuser"
          />

          <div className="space-y-3 border border-border/60 rounded-md p-4">
            <Label className="text-sm font-medium">Account heuristics</Label>
            <p className="text-xs text-muted-foreground">
              Items matched by these go to the Reviewed Queue, not Hidden — your attention, your
              rules.
            </p>

            <div className="space-y-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <Switch checked={fenceNewAccounts} onCheckedChange={setFenceNewAccounts} />
                <span className="text-sm flex-1">
                  <strong>Fence accounts younger than</strong>{' '}
                  <Input
                    type="number"
                    min={1}
                    max={3650}
                    className="inline-flex w-20 h-7 text-xs mx-1"
                    value={newAccountDays}
                    onChange={(e) => setNewAccountDays(Math.max(1, parseInt(e.target.value) || 30))}
                    disabled={!fenceNewAccounts}
                  />{' '}
                  days. <span className="text-muted-foreground">Bluesky only for now.</span>
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <Switch checked={fenceLowFollowers} onCheckedChange={setFenceLowFollowers} />
                <span className="text-sm flex-1">
                  <strong>Fence accounts with fewer than</strong>{' '}
                  <Input
                    type="number"
                    min={0}
                    className="inline-flex w-24 h-7 text-xs mx-1"
                    value={minFollowers}
                    onChange={(e) => setMinFollowers(Math.max(0, parseInt(e.target.value) || 0))}
                    disabled={!fenceLowFollowers}
                  />{' '}
                  followers. <span className="text-muted-foreground">Bluesky only for now.</span>
                </span>
              </label>
            </div>
          </div>

          <div>
            <Label>Platforms</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
              {PLATFORM_OPTIONS.map((p) => (
                <label key={p.value} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Switch
                    checked={platforms.includes(p.value)}
                    onCheckedChange={(checked) => togglePlatform(p.value, checked)}
                  />
                  <span>{p.label}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Leave all unchecked to apply across every connected source.
            </p>
          </div>

          <RssSourcePicker value={rssFeeds} onChange={setRssFeeds} />

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Update fence' : 'Create fence'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
